import React, {
	useEffect,
	useState,
	useMemo,
	useRef,
	useImperativeHandle,
	forwardRef,
} from "react";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import "./multiple.css";
import {
	ClipboardText,
	Eye,
	PencilSimple,
	ArrowSquareUp,
	ArrowSquareDown,
} from "@phosphor-icons/react";

const Multiple = ({
	goal,
	handleOpenLaunch,
	temPermissaoLancar,
	temPermissaoEditar,
	paramsBusca,
	currentYear,
	onEdit,
}) => {
	const navigate = useNavigate();
	const [lineChartData, setLineChartData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [maxValueLeft, setMaxValueLeft] = useState(undefined);
	const [minValueLeft, setMinValueLeft] = useState(undefined);
	const [maxValueRight, setMaxValueRight] = useState(undefined);
	const [minValueRight, setMinValueRight] = useState(undefined);
	const chartRef = useRef(null);

	const buildPeriods = (periodicity) => {
		const p = (periodicity || "").toLowerCase();
		switch (p) {
			case "bimestral":
				return [
					{ id: 1, label: "Jan-Fev", monthStart: 1 },
					{ id: 2, label: "Mar-Abr", monthStart: 3 },
					{ id: 3, label: "Mai-Jun", monthStart: 5 },
					{ id: 4, label: "Jul-Ago", monthStart: 7 },
					{ id: 5, label: "Set-Out", monthStart: 9 },
					{ id: 6, label: "Nov-Dez", monthStart: 11 },
				];
			case "trimestral":
				return [
					{ id: 1, label: "Jan-Mar", monthStart: 1 },
					{ id: 2, label: "Abr-Jun", monthStart: 4 },
					{ id: 3, label: "Jul-Set", monthStart: 7 },
					{ id: 4, label: "Out-Dez", monthStart: 10 },
				];
			case "quadrimestral":
				return [
					{ id: 1, label: "Jan-Abr", monthStart: 1 },
					{ id: 2, label: "Mai-Ago", monthStart: 5 },
					{ id: 3, label: "Set-Dez", monthStart: 9 },
				];
			case "semestral":
				return [
					{ id: 1, label: "Jan-Jun", monthStart: 1 },
					{ id: 2, label: "Jul-Dez", monthStart: 7 },
				];
			case "anual":
				return [{ id: 1, label: "Jan-Dez", monthStart: 1 }];
			case "mensal":
			default:
				return [
					{ id: 1, label: "Jan", monthStart: 1 },
					{ id: 2, label: "Fev", monthStart: 2 },
					{ id: 3, label: "Mar", monthStart: 3 },
					{ id: 4, label: "Abr", monthStart: 4 },
					{ id: 5, label: "Mai", monthStart: 5 },
					{ id: 6, label: "Jun", monthStart: 6 },
					{ id: 7, label: "Jul", monthStart: 7 },
					{ id: 8, label: "Ago", monthStart: 8 },
					{ id: 9, label: "Set", monthStart: 9 },
					{ id: 10, label: "Out", monthStart: 10 },
					{ id: 11, label: "Nov", monthStart: 11 },
					{ id: 12, label: "Dez", monthStart: 12 },
				];
		}
	};

	const periods = useMemo(
		() => buildPeriods(goal?.periodicity),
		[goal?.periodicity],
	);

	const metaConfig = useMemo(() => {
		return goal?.dados?.find((item) => item.goals_config?.type === "0")
			?.goals_config;
	}, [goal?.dados]);

	useEffect(() => {
		setLoading(true);

		const dadosOrganizados = goal.dados
			// Primeiro agrupamos por goals_config_id
			.reduce((agrupados, item) => {
				// Se não existir um grupo para este goals_config_id, criamos um
				if (!agrupados[item.goals_config_id]) {
					agrupados[item.goals_config_id] = [];
				}

				// Adicionamos o item ao grupo correspondente
				agrupados[item.goals_config_id].push(item);

				return agrupados;
			}, {});

		// Agora ordenamos cada grupo por month em ordem crescente
		Object.keys(dadosOrganizados).forEach((goalsConfigId) => {
			dadosOrganizados[goalsConfigId].sort((a, b) => {
				// Convertemos para número para garantir ordenação numérica correta
				return parseInt(a.month) - parseInt(b.month);
			});
		});

		// console.log(dadosOrganizados);

		const values = Object.entries(dadosOrganizados)
			// Ordenar por sequence antes de criar as séries (sequence vem como string)
			.sort(([goalsConfigIdA, itemsA], [goalsConfigIdB, itemsB]) => {
				const sequenceA = parseInt(itemsA[0]?.goals_config?.sequence || 0);
				const sequenceB = parseInt(itemsB[0]?.goals_config?.sequence || 0);
				return sequenceA - sequenceB;
			})
			.map(([goalsConfigId, items]) => {
				// O primeiro registro de cada grupo já está disponível diretamente
				const firstRecord = items[0];

				// Verifica se deve ser exibido no gráfico
				if (
					firstRecord.goals_config?.display_on_chart !== 1 &&
					firstRecord.goals_config?.display_on_chart !== 2
				) {
					return null;
				}

				const isSecondAxis =
					firstRecord.goals_config.show_on_second_axis === "1";

				const serie = {
					type:
						firstRecord.goals_config.display_on_chart === 2
							? "line"
							: "column",
					name: firstRecord.goals_config.name,
					data: periods.map((period) => {
						const found = items.find(
							(field) => parseInt(field.month) === period.monthStart,
						);
						return found ? parseFloat(found.value) : null;
					}),
					// Marca se usa eixo secundário (será usado na configuração do yaxis)
					useSecondAxis: isSecondAxis,
					decimals:
						firstRecord.goals_config?.decimals_cases != null
							? Number(firstRecord.goals_config.decimals_cases)
							: 2,
				};

				// Adiciona a cor apenas se estiver definida
				if (firstRecord.goals_config.color) {
					serie.color = firstRecord.goals_config.color;
				}

				return serie;
			})
			.filter(Boolean); // Remove null values

		// Calcular min/max de cada eixo separadamente
		// Extrair todos os valores numéricos das séries visíveis
		if (values.length > 0) {
			// Eixo esquerdo (principal)
			const leftSeriesValues = values
				.filter((s) => !s.useSecondAxis)
				.flatMap((s) => s.data)
				.filter(val => val !== null && val !== undefined && !isNaN(val));

			if (leftSeriesValues.length > 0) {
				const dataMin = Math.min(...leftSeriesValues);
				const dataMax = Math.max(...leftSeriesValues);
				
				if (dataMin >= 0) {
					// Todos positivos: garantir que zero apareça
					setMaxValueLeft(dataMax * 1.1);
					setMinValueLeft(0);
				} else if (dataMax <= 0) {
					// Todos negativos: garantir que zero apareça
					setMaxValueLeft(0);
					setMinValueLeft(dataMin * 1.1);
				} else {
					// Valores mistos: deixar ApexCharts calcular automaticamente (como no exemplo)
					setMaxValueLeft(undefined);
					setMinValueLeft(undefined);
				}
			}

			// Eixo direito (secundário)
			const rightSeriesValues = values
				.filter((s) => s.useSecondAxis)
				.flatMap((s) => s.data)
				.filter(val => val !== null && val !== undefined && !isNaN(val));

			if (rightSeriesValues.length > 0) {
				const dataMin = Math.min(...rightSeriesValues);
				const dataMax = Math.max(...rightSeriesValues);
				
				if (dataMin >= 0) {
					// Todos positivos: garantir que zero apareça
					setMaxValueRight(dataMax * 1.1);
					setMinValueRight(0);
				} else if (dataMax <= 0) {
					// Todos negativos: garantir que zero apareça
					setMaxValueRight(0);
					setMinValueRight(dataMin * 1.1);
				} else {
					// Valores mistos: deixar ApexCharts calcular automaticamente (como no exemplo)
					setMaxValueRight(undefined);
					setMinValueRight(undefined);
				}
			}
		}
		setLineChartData(values);
		setLoading(false);
	}, [goal]);

	const options = useMemo(
		() => ({
			chart: {
				events: {
					mounted(chartContext) {
						chartRef.current = chartContext;
					},
					dataPointSelection: () => {
						// Desabilita cliques nos pontos de dados
						return false;
					},
					legendClick: () => {
						// Desabilita cliques na legenda
						return false;
					},
					markerClick: () => {
						// Desabilita cliques nos marcadores
						return false;
					},
				},
				height: 400,
				toolbar: {
					show: false,
				},
				parentHeightOffset: 0,
				redrawOnParentResize: true,
				redrawOnWindowResize: true,
				selection: {
					enabled: false, // Desabilita seleção
				},
				zoom: {
					enabled: false, // Desabilita zoom
				},
				ignoreEmptySeries: false, // Não ignora séries com valores null
			},
			stroke: {
				curve: "straight",
				width: 2,
			},
			xaxis: {
				categories: periods.map((item) => item.label),
				title: {
					text: "Período",
				},
				crosshairs: {
					show: true,
					position: "back",
					stroke: {
						width: 1,
						dashArray: 4,
						color: "#999",
					},
				},
				tooltip: {
					enabled: false,
				}
			},
			yaxis: (() => {
				// Cria apenas 2 eixos: principal e secundário
				const yaxisConfig = [];

				// Eixo principal (esquerda) - para séries sem useSecondAxis
				const leftSeries = lineChartData.filter(serie => !serie.useSecondAxis);
				if (leftSeries.length > 0) {
					const yaxisConfigLeft = {
						seriesName: leftSeries.map(s => s.name), // Array com nomes das séries
						title: { text: "" },
						labels: {
							formatter: (value) => {
								if (value === null || value === undefined || isNaN(value)) {
									return "0,00";
								}
								// Arredonda valores muito próximos de zero para zero (evita -0,00)
								const normalizedValue = Math.abs(value) < 0.01 ? 0 : value;
								return normalizedValue.toLocaleString("pt-BR", {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								});
							},
						},
					};
					// Só define min/max se não forem undefined (valores mistos deixam undefined para ApexCharts calcular)
					if (minValueLeft !== undefined && maxValueLeft !== undefined) {
						yaxisConfigLeft.min = minValueLeft;
						yaxisConfigLeft.max = maxValueLeft;
					}
					yaxisConfig.push(yaxisConfigLeft);
				}

				// Eixo secundário (direita) - para séries com useSecondAxis
				const rightSeries = lineChartData.filter(serie => serie.useSecondAxis);
				if (rightSeries.length > 0) {
					const yaxisConfigRight = {
						seriesName: rightSeries.map(s => s.name), // Array com nomes das séries
						opposite: true,
						title: { text: "" },
						labels: {
							formatter: (value) => {
								if (value === null || value === undefined || isNaN(value)) {
									return "0,00";
								}
								// Arredonda valores muito próximos de zero para zero (evita -0,00)
								const normalizedValue = Math.abs(value) < 0.01 ? 0 : value;
								return (
									normalizedValue.toLocaleString("pt-BR", {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})
								);
							},
						},
					};
					// Só define min/max se não forem undefined (valores mistos deixam undefined para ApexCharts calcular)
					if (minValueRight !== undefined && maxValueRight !== undefined) {
						yaxisConfigRight.min = minValueRight;
						yaxisConfigRight.max = maxValueRight;
					}
					yaxisConfig.push(yaxisConfigRight);
				}

				return yaxisConfig;
			})(),
			legend: {
				position: "bottom",
				horizontalAlign: "center",
				fontSize: "10px",
				itemMargin: {
					horizontal: 4,
					vertical: 2
				},
				markers: {
					width: 6,
					height: 6,
					radius: 3
				}
			},
			plotOptions: {
				bar: {
					columnWidth: "80%",
					dataLabels: {
						position: "top",
					},
				},
			},
			tooltip: {
				enabled: true,
				intersect: false,
				shared: true,
				followCursor: true, // Tooltip segue o cursor suavemente
				custom: function ({ series, seriesIndex, dataPointIndex, w }) {
					// Criar tooltip customizado que sempre mostra todas as séries
					const monthName = w.globals.categoryLabels[dataPointIndex];
					let tooltipContent = `<div style="padding: 10px; background: white; border-radius: 5px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">`;
					tooltipContent += `<div style="font-weight: bold; margin-bottom: 8px; color: #333;">${monthName}</div>`;

					// Iterar por todas as séries
					series.forEach((serieData, index) => {
						// Verificar se a série existe e está ativa
						if (!serieData || !w.globals.seriesNames[index]) {
							return;
						}

						const serieName = w.globals.seriesNames[index];
						const value = serieData[dataPointIndex];

						// Só mostrar se o valor não for null/undefined e se a série estiver visível
						if (value !== null && value !== undefined && !isNaN(value)) {
							const decimals = Number(
								lineChartData[index]?.decimals ?? 2,
							);
							const formattedValue = value.toLocaleString("pt-BR", {
								minimumFractionDigits: decimals,
								maximumFractionDigits: decimals,
							});

							// Pegar a cor da série
							const color = w.globals.colors[index];

							tooltipContent += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
							tooltipContent += `<div style="width: 12px; height: 12px; background: ${color}; border-radius: 50%; margin-right: 8px;"></div>`;
							tooltipContent += `<span style="color: #333; font-size: 13px;">${serieName}: <strong>${formattedValue}</strong></span>`;
							tooltipContent += `</div>`;
						}
					});

					tooltipContent += `</div>`;
					return tooltipContent;
				}
			},
			markers: {
				size: 5,
				hover: {
					size: 7,
				},
			},
			responsive: [
				{
					breakpoint: 480,
					options: {
						legend: {
							fontSize: "8px",
						},
					},
				},
			],
		}),
		[maxValueLeft, minValueLeft, maxValueRight, minValueRight, lineChartData],
	);

	const isNewStructure = (goal?.structure_version || "legacy") === "variables";

	const editarItem = () => {
		// Indicador novo (estrutura de variáveis): edita pelo modal.
		if (isNewStructure && onEdit) {
			onEdit(goal.id);
			return;
		}

		// Indicador antigo (legacy): mantém a edição na página antiga.
		const filtersToSend = {
			name: paramsBusca?.name || "",
			organization_id: paramsBusca?.organization_id || "",
			year: currentYear || new Date().getFullYear(),
		};

		navigate(`form/${goal.id}`, {
			state: {
				filters: filtersToSend,
			},
		});
	};

	return (
		<Card>
			<CardBody>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px", width: "100%" }}>
					<h4 className="card-title mb-0">{goal?.intention_goals}</h4>
					{metaConfig?.type_meta_definition && (
						<div style={{
							display: "flex",
							alignItems: "center",
							gap: "5px",
							padding: "4px 8px",
							borderRadius: "4px",
							backgroundColor: metaConfig.type_meta_definition === "maior_melhor" ? "#e3f2fd" : "#ffebee",
							border: `1px solid ${metaConfig.type_meta_definition === "maior_melhor" ? "#0d6efd" : "#dc3545"}`,
							flexShrink: 0
						}}>
							{metaConfig.type_meta_definition === "maior_melhor" ? (
								<ArrowSquareUp size={16} color="#0d6efd" />
							) : (
								<ArrowSquareDown size={16} color="#dc3545" />
							)}
							<span style={{
								fontSize: "12px",
								color: metaConfig.type_meta_definition === "maior_melhor" ? "#0d6efd" : "#dc3545",
								fontWeight: "400",
								whiteSpace: "nowrap"
							}}>
								{metaConfig.type_meta_definition === "maior_melhor" ? "Maior Melhor" : "Menor Melhor"}
							</span>
						</div>
					)}
				</div>
				<Row>
					<Col lg="12" className="chart-container">
						<div id="line-chart" dir="ltr" style={{ width: "100%" }}>
							{loading ? (
								<span
									className="spinner-border spinner-border-sm"
									style={{
										margin: "0 auto",
										display: "flex",
										marginBottom: 20,
									}}
								></span>
							) : (
								<ReactApexChart
									series={lineChartData}
									options={options}
									height={370}
									className="apex-charts"
								/>
							)}
						</div>
						<div
							style={{ display: "flex" }}
							className="button-container"
						>
							{temPermissaoEditar && (
								<Button
									onClick={editarItem}
									style={{
										marginLeft: 5,
										background: "none",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "2px",
									}}
								>
									<PencilSimple size={16} /> Editar
								</Button>
							)}
							{temPermissaoLancar && !isNewStructure && (
								<Button
									key={`lancar-${goal.id}`}
									onClick={() => handleOpenLaunch(goal.id)}
									style={{
										marginLeft: 5,
										background: "none",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "2px",
									}}
								>
									<ClipboardText size={16} /> Lançar
								</Button>
							)}
							<Button
								onClick={() => {
									const baseUrl =
										window.location.origin +
										window.location.pathname;
									let yearParam =
										currentYear || new Date().getFullYear();
									let url = `${baseUrl}#/indicadores/visualizar?id=${goal.id}&year=${yearParam}`;
									if (paramsBusca) {
										url += `&data=${JSON.stringify(
											paramsBusca,
										)}`;
									}
									window.open(url, "_blank");
								}}
								style={{
									marginLeft: 5,
									background: "none",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "2px",
								}}
							>
								<Eye size={16} /> Visualizar
							</Button>
						</div>
					</Col>
				</Row>
			</CardBody>
		</Card>
	);
};

export default Multiple;
